import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type { Webhook } from '../../lib/types';
import { useStore } from '../../store/useStore';
import { ErrorLine, Section } from './ui';

// --- Discord webhooks -------------------------------------------------------

export function IntegrationsTab() {
  const groupId = useStore((s) => s.activeGroupId);

  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [tested, setTested] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    const result = await api<{ webhooks: Webhook[] }>(`/api/groups/${groupId}/webhooks`);
    setWebhooks(result.webhooks);
  }, [groupId]);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  async function add(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api(`/api/groups/${groupId}/webhooks`, { method: 'POST', body: { url, label } });
      setUrl('');
      setLabel('');
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not add the webhook');
    }
  }

  async function test(webhookId: string) {
    setTested((prev) => ({ ...prev, [webhookId]: 'testing' }));
    const result = await api<{ ok: boolean }>(
      `/api/groups/${groupId}/webhooks/${webhookId}/test`,
      { method: 'POST' },
    ).catch(() => ({ ok: false }));
    setTested((prev) => ({ ...prev, [webhookId]: result.ok ? 'ok' : 'failed' }));
    await load();
  }

  async function toggle(webhook: Webhook) {
    await api(`/api/groups/${groupId}/webhooks/${webhook.id}`, {
      method: 'PATCH',
      body: { isActive: !webhook.isActive },
    });
    await load();
  }

  async function remove(webhookId: string) {
    await api(`/api/groups/${groupId}/webhooks/${webhookId}`, { method: 'DELETE' });
    await load();
  }

  return (
    <Section
      title="Discord webhooks"
      description="Every active webhook receives the changelog when a version is released."
    >
      <div className="flex flex-col gap-2">
        {webhooks.map((webhook) => (
          <div key={webhook.id} className="card flex flex-wrap items-center gap-3 p-4">
            <span
              className={`size-2 shrink-0 rounded-full ${webhook.isActive ? 'bg-done' : 'bg-todo'}`}
              title={webhook.isActive ? 'Active' : 'Paused'}
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{webhook.label || 'Discord channel'}</p>
              <p className="truncate font-mono text-[11px] text-ink-faint">{webhook.maskedUrl}</p>
            </div>
            <span className="ml-auto flex shrink-0 items-center gap-2">
              {tested[webhook.id] === 'ok' && <span className="text-xs text-done">Sent</span>}
              {tested[webhook.id] === 'failed' && <span className="text-xs text-danger">Failed</span>}
              {webhook.lastStatus === 'failed' && !tested[webhook.id] && (
                <span className="text-xs text-blocked">Last delivery failed</span>
              )}
              <button
                type="button"
                onClick={() => void test(webhook.id)}
                className="btn btn-ghost !py-1 !text-xs"
                disabled={tested[webhook.id] === 'testing'}
              >
                {tested[webhook.id] === 'testing' ? 'Sending…' : 'Test'}
              </button>
              <button
                type="button"
                onClick={() => void toggle(webhook)}
                className="btn btn-ghost !py-1 !text-xs"
              >
                {webhook.isActive ? 'Pause' : 'Resume'}
              </button>
              <button
                type="button"
                onClick={() => void remove(webhook.id)}
                className="btn btn-danger !py-1 !text-xs"
              >
                Remove
              </button>
            </span>
          </div>
        ))}
      </div>

      <form onSubmit={add} className="flex flex-wrap gap-2">
        <input
          className="field-input min-w-64 flex-1 font-mono text-xs"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://discord.com/api/webhooks/…"
          required
          maxLength={500}
        />
        <input
          className="field-input !w-auto text-sm"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="#releases"
          maxLength={80}
        />
        <button type="submit" className="btn btn-ghost">
          Add webhook
        </button>
      </form>

      <ErrorLine message={error} />
    </Section>
  );
}
